// Two-step upload: ask the backend for a presigned URL, then PUT the bytes
// directly. In production the second PUT goes to Cloudflare R2 and never
// touches our FastAPI service; in dev it lands on the local backend.

import { api } from "./client";

export type UploadPurpose = "profile" | "card-logo" | "bg";

export interface UploadResponse {
  key: string;
  presigned_url: string;
  public_url: string;
  method: "PUT";
  headers: Record<string, string> | null;
  max_bytes: number;
}

export async function createUpload(input: {
  purpose: UploadPurpose;
  filename: string;
  content_type: string;
  size: number;
}): Promise<UploadResponse> {
  return api.post<UploadResponse>("/me/uploads", input as unknown as Record<string, unknown>);
}

export async function uploadFile(file: File, purpose: UploadPurpose): Promise<string> {
  // Send the exact byte count up front. The backend rejects oversized
  // requests with 422 before signing, AND bakes Content-Length into the
  // presigned URL — so R2 enforces it even if the browser were lying.
  const creds = await createUpload({
    purpose,
    filename: file.name,
    content_type: file.type || "application/octet-stream",
    size: file.size,
  });
  const put = await fetch(creds.presigned_url, {
    method: creds.method,
    headers: creds.headers ?? undefined,
    body: file,
  });
  if (!put.ok) {
    throw new Error(`Upload failed (${put.status})`);
  }
  return creds.public_url;
}
